import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ProviderManualControlModal from './ProviderManualControlModal';
import {
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  PauseCircle,
  PlayCircle,
  Search,
  Filter,
  Mail,
  Eye,
  Phone,
  MapPin,
  DollarSign,
  Calendar,
  AlertCircle,
  TrendingUp,
  Users,
  Ban,
  Shield,
  RefreshCw,
  FileText,
  CreditCard,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { useAdminProviders } from '@/hooks/useAdminProviders';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

export default function AdminProvidersPanel() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: 'all' as any,
    paymentStatus: 'all' as any,
    verified: 'all' as any,
    search: ''
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update filters when debounced search changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, search: debouncedSearch }));
  }, [debouncedSearch]);

  const {
    providers,
    stats,
    isLoading,
    verifyProvider,
    rejectProvider,
    updateProvider,
    deleteProvider,
    suspendProvider,
    reactivateProvider,
    grantProviderAccess,
    updateProviderDates,
    applyDiscount,
    syncPayPalStatus,
    isVerifying,
    isRejecting,
    isUpdating,
    isDeleting,
    isGrantingAccess,
    isUpdatingDates,
    isApplyingDiscount,
    isSyncingPayPal
  } = useAdminProviders(filters);

  const { sendPrivate } = useAdminNotifications();
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [manualControlModal, setManualControlModal] = useState<{
    isOpen: boolean;
    action: 'grant_access' | 'update_dates' | 'apply_discount' | null;
    providerId: string;
    providerName: string;
  }>({
    isOpen: false,
    action: null,
    providerId: '',
    providerName: ''
  });

  const getStatusBadge = (status: string) => {
    const statusColors: any = {
      active: 'bg-green-500',
      pending: 'bg-yellow-500',
      pending_payment: 'bg-orange-500',
      pending_verification: 'bg-blue-500',
      suspended: 'bg-red-500',
      cancelled: 'bg-gray-500',
      expired: 'bg-gray-400',
      rejected: 'bg-red-600'
    };

    return (
      <Badge className={`${statusColors[status] || 'bg-gray-500'} text-white`}>
        {status.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPaymentBadge = (status: string) => {
    const colors: any = {
      active: 'bg-green-500',
      pending: 'bg-yellow-500',
      cancelled: 'bg-red-500',
      expired: 'bg-gray-500',
      failed: 'bg-red-600'
    };

    return (
      <Badge className={`${colors[status] || 'bg-gray-500'} text-white`}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  const handleSendNotification = async (provider: any) => {
    if (!notificationMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      sendPrivate.mutate({
        targetUserId: provider.user_id,
        title: 'Admin Notification',
        message: notificationMessage
      });

      toast.success('Notification sent successfully');
      setNotificationMessage('');
      setSelectedProvider(null);
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t('services.adminProviders.statistics.total')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-500" />
              {t('services.adminProviders.statistics.pending')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              {t('services.adminProviders.statistics.active')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.active || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Ban className="w-4 h-4 text-red-500" />
              {t('services.adminProviders.statistics.suspended')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.suspended || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-orange-500" />
              {t('services.adminProviders.statistics.paymentIssues')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.paymentIssues || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            {t('services.adminProviders.filters.title', 'Filters')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search bar - Full width on top */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t('services.adminProviders.filters.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('services.adminProviders.filters.searchPlaceholder', 'Search by name or email...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Other filters below */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t('services.adminProviders.filters.subscriptionStatus')}</label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('services.adminProviders.filters.allStatuses', 'All Statuses')}</SelectItem>
                    <SelectItem value="pending_verification">{t('services.adminProviders.statistics.pending')}</SelectItem>
                    <SelectItem value="active">{t('services.adminProviders.status.active')}</SelectItem>
                    <SelectItem value="suspended">{t('services.adminProviders.status.suspended')}</SelectItem>
                    <SelectItem value="cancelled">{t('services.adminProviders.status.cancelled')}</SelectItem>
                    <SelectItem value="pending_payment">{t('services.adminProviders.filters.pendingPayment', 'Pending Payment')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">{t('services.adminProviders.filters.paymentStatus')}</label>
                <Select
                  value={filters.paymentStatus}
                  onValueChange={(value) => setFilters({ ...filters, paymentStatus: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('services.adminProviders.filters.allPayments', 'All Payments')}</SelectItem>
                    <SelectItem value="active">{t('services.adminProviders.status.active')}</SelectItem>
                    <SelectItem value="pending">{t('services.adminProviders.status.pending')}</SelectItem>
                    <SelectItem value="cancelled">{t('services.adminProviders.status.cancelled')}</SelectItem>
                    <SelectItem value="expired">{t('services.adminProviders.status.expired')}</SelectItem>
                    <SelectItem value="failed">{t('services.adminProviders.status.failed', 'Failed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">{t('services.adminProviders.filters.verification')}</label>
                <Select
                  value={String(filters.verified)}
                  onValueChange={(value) => setFilters({ ...filters, verified: value === 'all' ? 'all' : value === 'true' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('services.adminProviders.filters.all', 'All')}</SelectItem>
                    <SelectItem value="true">{t('services.adminProviders.filters.verified', 'Verified')}</SelectItem>
                    <SelectItem value="false">{t('services.adminProviders.filters.notVerified', 'Not Verified')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Providers List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('services.adminProviders.providersList', 'Service Providers')} ({providers?.length || 0})</CardTitle>
          <CardDescription>{t('services.adminProviders.providersListDesc', 'Manage all service provider accounts')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {providers?.map((provider) => (
              <Card key={provider.id} className="border-2 overflow-hidden">
                <CardContent className="p-0">
                  {/* Header Section with Name and Status */}
                  <div className="bg-muted/30 p-4 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold">{provider.business_name}</h3>
                        {provider.verified && (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        {getStatusBadge(provider.subscription_status)}
                        {getPaymentBadge(provider.provider_payment_status)}
                        <Badge variant="outline">{provider.provider_type.replace(/_/g, ' ')}</Badge>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-background border-b">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{provider.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span>{provider.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span>{provider.city}, {provider.state}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span>{format(new Date(provider.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>

                  {/* Metrics Bar */}
                  <div className="grid grid-cols-4 gap-4 p-4 bg-muted/10 border-b">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-1">{t('services.adminProviders.metrics.rating', 'Rating')}</p>
                      <p className="font-semibold">⭐ {provider.rating}/5</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-1">{t('services.adminProviders.metrics.reviews', 'Reviews')}</p>
                      <p className="font-semibold">{provider.total_reviews}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-1">{t('services.adminProviders.metrics.views', 'Views')}</p>
                      <p className="font-semibold">{provider.total_views}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-1">{t('services.adminProviders.metrics.contacts', 'Contacts')}</p>
                      <p className="font-semibold">{provider.total_contacts}</p>
                    </div>
                  </div>

                  {/* Primary Actions - Verification Required */}
                  {!provider.verified && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 border-b">
                      <p className="text-sm font-medium mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {t('services.adminProviders.verificationRequired', 'Verification Required')}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => verifyProvider(provider.id)}
                          disabled={isVerifying}
                          className="bg-green-600 hover:bg-green-700 flex-1"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {t('services.adminProviders.actions.verifyProvider', 'Verify Provider')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => rejectProvider(provider.id)}
                          disabled={isRejecting}
                          className="flex-1"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          {t('services.adminProviders.actions.reject')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Verification Documents Section - Admin Only */}
                  {(provider.id_document_front_url || provider.id_document_back_url || provider.credential_document_url || provider.registration_number) && (
                    <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border-b">
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {t('services.adminProviders.documents.title', 'Verification Documents')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {provider.id_document_front_url && (
                          <div className="flex items-center gap-2 p-2 bg-background rounded border overflow-hidden">
                            <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-muted">
                              <img
                                src={provider.id_document_front_url}
                                alt="ID Front"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium block truncate">{t('services.adminProviders.documents.idFront', 'ID Document (Front)')}</span>
                              <span className="text-xs text-green-600">✓ {t('services.adminProviders.documents.uploaded', 'Uploaded')}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 flex-shrink-0"
                              onClick={() => window.open(provider.id_document_front_url, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        {provider.id_document_back_url && (
                          <div className="flex items-center gap-2 p-2 bg-background rounded border overflow-hidden">
                            <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-muted">
                              <img
                                src={provider.id_document_back_url}
                                alt="ID Back"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium block truncate">{t('services.adminProviders.documents.idBack', 'ID Document (Back)')}</span>
                              <span className="text-xs text-green-600">✓ {t('services.adminProviders.documents.uploaded', 'Uploaded')}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 flex-shrink-0"
                              onClick={() => window.open(provider.id_document_back_url, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        {provider.credential_document_url && (
                          <div className="flex items-center gap-2 p-2 bg-background rounded border overflow-hidden">
                            <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-muted">
                              <img
                                src={provider.credential_document_url}
                                alt="Credential"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg></div>';
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium block truncate">{t('services.adminProviders.documents.credential', 'Professional Credential')}</span>
                              <span className="text-xs text-green-600">✓ {t('services.adminProviders.documents.uploaded', 'Uploaded')}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 flex-shrink-0"
                              onClick={() => window.open(provider.credential_document_url, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        {provider.registration_number && (
                          <div className="flex items-center gap-2 p-2 bg-background rounded border">
                            <Shield className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm flex-1">{t('services.adminProviders.documents.registrationNumber', 'Registration #')}: {provider.registration_number}</span>
                          </div>
                        )}
                      </div>
                      {!provider.verified && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {t('services.adminProviders.documents.reviewNote', 'Review these documents before verifying the provider')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Admin Manual Control Section */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border-b">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      {t('services.adminProviders.manualControl.title', 'Admin Manual Control')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setManualControlModal({
                          isOpen: true,
                          action: 'grant_access',
                          providerId: provider.id,
                          providerName: provider.business_name
                        })}
                        disabled={provider.manually_activated}
                        className="bg-background"
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        {t('services.adminProviders.manualControl.grantAccess', 'Grant Access')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setManualControlModal({
                          isOpen: true,
                          action: 'update_dates',
                          providerId: provider.id,
                          providerName: provider.business_name
                        })}
                        className="bg-background"
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        {t('services.adminProviders.manualControl.editDates', 'Edit Dates')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setManualControlModal({
                          isOpen: true,
                          action: 'apply_discount',
                          providerId: provider.id,
                          providerName: provider.business_name
                        })}
                        className="bg-background"
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        {t('services.adminProviders.manualControl.discount', 'Discount')}
                      </Button>
                      {provider.paypal_provider_subscription_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => syncPayPalStatus(provider.id)}
                          disabled={isSyncingPayPal}
                          className="bg-background"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          {t('services.adminProviders.manualControl.syncPayPal', 'Sync PayPal')}
                        </Button>
                      )}
                    </div>
                    {provider.manually_activated && (
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        {t('services.adminProviders.manualControl.manuallyActivated', 'Manually activated by admin')}
                      </p>
                    )}
                    {provider.paypal_provider_subscription_id && (
                      <p className="text-xs text-muted-foreground mt-2">
                        PayPal ID: {provider.paypal_provider_subscription_id}
                      </p>
                    )}
                  </div>

                  {/* Management Actions */}
                  <div className="p-4 flex flex-wrap gap-2">
                    {provider.subscription_status !== 'suspended' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => suspendProvider(provider.id)}
                      >
                        <PauseCircle className="w-4 h-4 mr-2" />
                        {t('services.adminProviders.actions.suspend')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => reactivateProvider(provider.id)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        {t('services.adminProviders.actions.reactivate')}
                      </Button>
                    )}

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" onClick={() => setSelectedProvider(provider)}>
                          <Mail className="w-4 h-4 mr-2" />
                          {t('services.adminProviders.actions.notifyProvider', 'Notify Provider')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('services.adminProviders.actions.sendNotification')}</DialogTitle>
                          <DialogDescription>
                            {t('services.adminProviders.notificationDesc', 'Send a notification to')} {provider.business_name}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <Input
                            placeholder={t('services.adminProviders.notificationPlaceholder', 'Notification message...')}
                            value={notificationMessage}
                            onChange={(e) => setNotificationMessage(e.target.value)}
                          />
                          <Button onClick={() => handleSendNotification(provider)} className="w-full">
                            {t('services.adminProviders.actions.sendNotification')}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        if (confirm(t('services.adminProviders.confirmDelete', 'Are you sure you want to delete this provider?'))) {
                          deleteProvider(provider.id);
                        }
                      }}
                      disabled={isDeleting}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('services.adminProviders.actions.delete')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {providers?.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('services.adminProviders.noProviders', 'No providers found matching your filters')}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual Control Modal */}
      <ProviderManualControlModal
        isOpen={manualControlModal.isOpen}
        onClose={() => setManualControlModal({ isOpen: false, action: null, providerId: '', providerName: '' })}
        providerId={manualControlModal.providerId}
        providerName={manualControlModal.providerName}
        action={manualControlModal.action}
        onGrantAccess={(id, reason) => grantProviderAccess({ providerId: id, reason })}
        onUpdateDates={(id, start, end) => updateProviderDates({
          providerId: id,
          subscription_start_date: start,
          subscription_end_date: end
        })}
        onApplyDiscount={(id, discount, type, notes) => applyDiscount({
          providerId: id,
          discount_applied: discount,
          discount_type: type,
          special_plan_notes: notes
        })}
        isLoading={isGrantingAccess || isUpdatingDates || isApplyingDiscount}
      />
    </div>
  );
}
